import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { post, get, del, patch, put } from '../api/api';
import { BASEURL } from '../api/constants';
import { useAuth } from './authContext';

const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
  const { user } = useAuth();
  const isLoading = false;
  const error = null;

  const [token, setToken] = useState(null);
  // Request de-duplication and result caching for image/details fetches
  const inFlightImageDetailRequests = useRef(new Map());
  const imageDetailResultCache = useRef(new Map());

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    } else if (user?.token) {
      setToken(user.token);
    }
  }, [user]);

  //Users
  const allUsers = async () => {
    try {
      const res = await get('', token);

      if (Array.isArray(res)) {
        return { success: true, data: res };
      } else {
        return { success: false, error: res?.message || 'Invalid user data received.' };
      }
    } catch (error) {
      return { success: false, error: 'Network error or invalid API response' };
    }
  };

  const deleteUser = async (id) => {
    try {
      const res = await del(`/${id.id}`, token);

      if (res.message) {
        return { success: true, data: res };
      } else {
        return { success: false, error: res?.message || 'Invalid user data received.' };
      }
    } catch (error) {
      return { success: false, error: 'Network error or invalid API response' };
    }
  };

  const CreateUser = async (formData) => {
    try {
      const res = await post(
        '',
        {
          email: formData.email,
          password: formData.password,
          name: formData.user,
        },
        token
      );

      if (res.id) {
        return { success: true };
      } else {
        return { success: false, error: res?.data?.message || 'Something went wrong' };
      }
    } catch (error) {
      return { success: false, error: 'Network error or invalid API response' };
    }
  };

  const updateUserPassword = async (id, newPassword) => {
    try {
      // Pass both ID and newPassword in the body
      const res = await patch('/password', { id: id.id, newPassword }, token);
      if (res.message) {
        return { success: true };
      } else {
        return { success: false, error: res?.message || 'Invalid user data received.' };
      }
    } catch {
      return { success: false, error: 'Failed to update password' };
    }
  };

  const changeOwnPassword = async (newPassword) => {
    try {
      const res = await patch('/password', { newPassword }, token);
      if (res.message) {
        return { success: true };
      } else {
        return { success: false, error: res?.message || 'Invalid user data received.' };
      }
    } catch (error) {
      return { success: false, error: 'Failed to update password' };
    }
  };
  
  // Customers

  const createCustomer = async (customerData) => {
    try {
      const res = await post('/customers', customerData, token);

      console.log('[DEBUG] Raw response from POST /customers:', res);

      const data = res?.data || res;

      return data?._id
        ? { success: true, data }
        : { success: false, error: data?.error || 'Failed to create customer' };
    } catch (err) {
      console.error('createCustomer error:', err.message || err);
      return { success: false, error: err.message || 'Network error while creating customer' };
    }
  };

  const getAllCustomers = async () => {
    try {
      // const res = await get(`/customers`, token);
      const res = await get(`global?type=customers`, token);
      return Array.isArray(res)
        ? { success: true, data: res }
        : { success: false, error: res?.message || 'Invalid customer data' };
    } catch {
      return { success: false, error: 'Network error' };
    }
  };

  const updateCustomer = async (id, customerName) => {
    try {
      const res = await patch(`/customers/${id}`, { customer: customerName }, token);

      if (res._id) {
        return { success: true };
      } else {
        return { success: false };
      }
    } catch {
      return { success: false, error: 'Failed to update customer' };
    }
  };

  const updateCustomerNotes = async (id, text) => {
    try {
      const res = await patch(`/customers/${id}/notes`, { text }, token);

      if (res._id) {
        return { success: true };
      } else {
        return { success: false };
      }
    } catch {
      return { success: false, error: 'Failed to update notes' };
    }
  };

  const getQuotePdf = async (quoteNumber, presign = true) => {
    try {
      const res = await get(
        `/quotes/${quoteNumber}/pdf?presign=${presign}`,
        token
      );

      // Backend now returns proper JSON responses
      if (res.success) {
        if (presign) {
          return { success: true, url: res.url };
        } else {
          return { success: true, blob: res };
        }
      } else {
        return { success: false, error: res.error || "PDF not found" };
      }
    } catch (err) {
      console.error("Failed to fetch quote PDF:", err);
      
      // Handle HTTP errors
      if (err.response?.status === 404) {
        return { success: false, error: "PDF not found" };
      }
      
      return { success: false, error: "Failed to fetch quote PDF" };
    }
  };

  // Append a single additional_location to a customer.
  // Why: contact-dropdown lets the user add a new contact inline while building a quote;
  // we don't want to make them save the whole customer record from this flow.
  const addCustomerLocation = async (id, location) => {
    try {
      const res = await patch(`/customers/${id}/additional_locations`, location, token);
      if (res?._id || res?.success) {
        return { success: true, data: res };
      }
      return { success: false, error: res?.error || res?.message || 'Failed to add contact' };
    } catch (err) {
      console.error('addCustomerLocation error:', err.message || err);
      return { success: false, error: err.message || 'Network error while adding contact' };
    }
  };

  const updateCustomerFull = async (id, payload) => {
    try {
      const res = await patch(`/customers/${id}/full`, payload, token);
      if (res._id) {
        return { success: true };
      } else {
        return { success: false };
      }
    } catch {
      return { success: false, error: 'Failed to update full customer' };
    }
  };

  const deleteCustomer = async (id) => {
    try {
      const res = await del(`/customers/${id}`, token);
      if (res.message) {
        return { success: true };
      } else {
        return { success: false };
      }
    } catch {
      return { success: false, error: 'Failed to delete customer' };
    }
  };

  // Products
  const getAllProducts = async () => {
    try {
      // const res = await get(`/products`, token);
      const res = await get(`/global?type=products`, token);
      return { success: true, data: res };
    } catch {
      return { success: false, error: 'Failed to fetch products' };
    }
  };

  const getSawProducts = async () => {
    try {
      const res = await get(`/products/saws`, token);
      return { success: true, data: res };
    } catch {
      return { success: false, error: 'Failed to fetch saw products' };
    }
  };

  // Saws
  const getAllSaws = async () => {
    try {
      // const res = await get(`/saws`, token);
      const res = await get(`/global?type=saws`, token);
      return { success: true, data: res };
    } catch {
      return { success: false, error: 'Failed to fetch saws' };
    }
  };

const createSaw = async (sawData, imageFile, pdfFile) => {
  try {

    // Create FormData for file uploads
    const formData = new FormData();

    // Append all saw data fields to FormData
    Object.keys(sawData).forEach(key => {
      if (sawData[key] !== null && sawData[key] !== undefined) {
        formData.append(key, sawData[key].toString());
      }
    });

    // Append image file if provided
    if (imageFile) {
      formData.append('image', imageFile);
    }

    // Append PDF file if provided
    if (pdfFile) {
      formData.append('pdf', pdfFile);
    }

    const res = await post( '/saws', formData, token );

    console.log('📥 Backend response:', res);

    if (res && res._id) {
      console.log('✅ Successfully created saw:', res._id);
      return { success: true, saw: res };
    }
    else { console.log('❌ Invalid response:', res); return { success: false, error: 'Failed to create saw - invalid response' };
    }
  } catch (error) {
    console.error('❌ Error creating saw:', error);
    return { success: false, error: error.message || 'Failed to create saw' };
  }
};

  const updateSaw = async (id, payload, image, pdf) => {
    try {
      const formData = new FormData();
      
      // Append all non-file fields to FormData
      Object.keys(payload).forEach(key => {
        formData.append(key, payload[key]);
      });
      
      // Handle image file if present
      if (image) {
        formData.append('image', image);
      }

      // Handle PDF file if present
      if (pdf) {
        // Validate brochure filename is provided
        if (!payload.brochure) {
          return { 
            success: false, 
            error: 'Brochure filename is required when updating PDF' 
          };
        }
        
        formData.append('pdf', pdf);
      }

      const res = await patch(`/saws/${id}`, formData, token, 'PATCH');
      
      if (res._id) {
        return { success: true };
      } else {
        return { success: false };
      }
    } catch (err) {
      console.error(err);
      return { success: false, error: 'Failed to update saw' };
    }
  };

  // List the Cosen library PDFs (from the babs-drive-sync bucket, via the API).
  const getCosenLibrary = async () => {
    try {
      const res = await get('/saws/cosen-library', token);
      if (res && Array.isArray(res.items)) {
        return { success: true, items: res.items };
      }
      return { success: false, error: res?.error || 'Failed to load library' };
    } catch (err) {
      console.error(err);
      return { success: false, error: 'Failed to load library' };
    }
  };

  // Fetch one Cosen library PDF's bytes (streamed through the API). Returns an
  // ArrayBuffer for loading into the PDF editor. Uses a raw fetch because the API
  // wrapper parses JSON, and this response is binary.
  const getCosenFile = async (key) => {
    try {
      const res = await fetch(`${BASEURL}/saws/cosen-library/file?key=${encodeURIComponent(key)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        return { success: false, error: `Failed to fetch file (${res.status})` };
      }
      const data = await res.arrayBuffer();
      return { success: true, data };
    } catch (err) {
      console.error(err);
      return { success: false, error: 'Failed to fetch file' };
    }
  };

  // Upload a (possibly edited) brochure PDF for a QUOTE. Stores it in
  // babs-images/brochures/ and returns the filename — does NOT touch any saw record.
  const uploadQuoteBrochure = async (file, filename) => {
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('filename', filename);
      const res = await post('/quotes/brochure-upload', formData, token);
      if (res && res.success) return { success: true, filename: res.filename };
      return { success: false, error: res?.error || 'Failed to upload brochure' };
    } catch (err) {
      console.error(err);
      return { success: false, error: 'Failed to upload brochure' };
    }
  };

  // Fetch a quote brochure's bytes (streamed through the API, no S3 CORS needed) for
  // loading into the PDF editor. Returns an ArrayBuffer.
  const getBrochureFile = async (pdfName) => {
    try {
      const res = await fetch(`${BASEURL}/quotes/brochure-file?pdfName=${encodeURIComponent(pdfName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return { success: false, error: `Failed to fetch brochure (${res.status})` };
      const data = await res.arrayBuffer();
      return { success: true, data };
    } catch (err) {
      console.error(err);
      return { success: false, error: 'Failed to fetch brochure' };
    }
  };

  const deleteSaw = async (id) => {
    try {
      const res = await del(`/saws/${id}`, token);
      if (res.message) {
        return { success: true, data: res };
      } else {
        return { success: false, error: res?.message || 'Invalid user data received.' };
      }
    } catch {
      return { success: false, error: 'Failed to delete saw' };
    }
  };

  // Quotes
  const getAllQuotes = async () => {
    try {
      // const res = await get(`/quotes`, token);
      const res = await get(`/global?type=quotes`, token);
      return { success: true, data: res };
    } catch {
      return { success: false, error: 'Failed to fetch quotes' };
    }
  };

  // Update a Quote
const updateQuoteVersion = async (quoteNumber, updateData) => {
  try {
    const res = await patch(
      `/quotes/quote/${encodeURIComponent(quoteNumber)}`,
      updateData,
      token
    );

    return res?.quote
      ? { success: true, data: res.quote }
      : { success: false, error: 'Failed to update quote: No data returned.' };

  } catch (err) {
    console.error('updateQuoteVersion error:', err.message || err);
    return {
      success: false,
      error: err.message || 'An unexpected error occurred while updating the quote.',
    };
  }
};

const deleteQuote = async (quote_number) => {
  try {
    const res = await del(
      `/quotes/quote-delete?quote_number=${encodeURIComponent(quote_number)}`,
      token
    );

    if (res.message && res.message.includes('deleted successfully')) {
      return { success: true };
    }
    
    return { success: true };

  } catch (error) {
    return { success: false, error: 'Failed to delete quote' };
  }
};

  // Global Get All / Multiple types data
  const getMultipleData = async (types = []) => {
  try {
    if (!types.length) {
      return { success: false, error: 'No types provided' };
    }

    const queryKey = types.length === 1 ? 'type' : 'types';
    const queryValue = types.join(',');
    const endpoint = `/global?${queryKey}=${queryValue}`;
    
    const res = await get(endpoint, token);

    if (types.length === 1) {
      return Array.isArray(res)
        ? { success: true, data: res }
        : { success: false, error: res?.message || 'Invalid response format' };
    }

    if (typeof res === 'object' && !Array.isArray(res)) {
      return { success: true, data: res };
    } else {
      return { success: false, error: 'Unexpected data structure' };
    }

    } catch (err) {
      console.error('getMultipleData error:', err.message || err);
      return { success: false, error: err.message || 'An unexpected error occurred while fetching data.' };
    }
};
  //                                          Quotes
  // Create a new quote
  const createQuote = async (data) => {
    try {
      const res = await post(`/quotes`, data, token);
      return res?._id
        ? { success: true, data: res }
        : { success: false, error: 'Failed to create quote: No ID returned.' };
    } catch (err) {
      console.error('createQuote error:', err.message || err);
      return {
        success: false,
        error: err.message || 'An unexpected error occurred while creating the quote.',
      };
    }
  };

  // getQuoteById — fetch a quote by its MongoDB _id, with token
  const getQuoteById = async (quoteId) => {
    try {
      const res = await get(`/quotes/quote/id/${encodeURIComponent(quoteId)}`, token);

      return res
        ? { success: true, data: res }
        : { success: false, error: 'Failed to fetch quote: No data returned.' };

    } catch (err) {
      console.error('getQuoteById error:', err.message || err);
      return {
        success: false,
        error: err.message || 'An unexpected error occurred while fetching the quote.',
      };
    }
  };

  // getQuoteNumber
  const getQuoteNumber = async (userName) => {
    try {
      const res = await get(`/quotes/generate-quote-number?userName=${encodeURIComponent(userName)}`);
      return res?.quoteNumber
        ? { success: true, data: res.quoteNumber }
        : { success: false, error: 'Failed to get quote number: No quoteNumber returned.' };
    } catch (err) {
      console.error('getQuoteNumber error:', err.message || err);
      return {
        success: false,
        error: err.message || 'An unexpected error occurred while fetching the quote number.',
      };
    }
  };

  // Create a new version/revision of a quote - Only patch the latest version!
  const createQuoteRevision = async (quoteNumber, data) => {
    try {
      const res = await patch(`/quotes/versioned?quote_number=${quoteNumber}`, data, token);
      return res?._id
        ? { success: true, data: res }
        : { success: false, error: 'Failed to create revision: No ID returned.' };
    } catch (err) {
      console.error('createQuoteRevision error:', err.message || err);
      return {
        success: false,
        error: err.message || 'An unexpected error occurred while creating quote revision.',
      };
    }
  };

const updateOrCreateQuote = async (quoteNumber, updateData) => {
  try {
    const token = localStorage.getItem('token');
    const res = await patch(`/quotes/update/${encodeURIComponent(quoteNumber)}`, updateData, token);

    console.log('API Response:', res);

    const isSuccess = res?.success && (res?.quote?._id || res?._id);
    const returnedData = res?.quote || res?.data || res;

    if (isSuccess) {
      return {
        success: true,
        data: returnedData,
        message: res.message || 'Quote processed successfully',
      };
    }

    return {
      success: false,
      error: res?.error || 'Unexpected response from server',
    };
  } catch (err) {
    console.error('API Error:', err);
    return {
      success: false,
      error: err.response?.data?.error || err.message || 'Network error',
    };
  }
};


  // Get all versions of a single quote
  const getQuoteVersions = async (quoteNumber) => {
    try {
      const res = await get(`/quotes/quote-versions?quote_number=${quoteNumber}`, token);
      return res?.versions
        ? { success: true, data: res }
        : { success: false, error: res?.error || 'No versions found for the provided quote number.' };
    } catch (err) {
      console.error('getQuoteVersions error:', err.message || err);
      return {
        success: false,
        error: err.message || 'An unexpected error occurred while fetching quote versions.',
      };
    }
  };

  // Get a single quote by quote_number or a single version of a quote by its version id / number
  const getQuoteByNumber = async (quoteNumber) => {
    try {
      const res = await get(`/quotes/quote?quote_number=${quoteNumber}`, token);
      return res?._id
        ? { success: true, data: res }
        : { success: false, error: res?.error || 'Quote not found for the provided quote number.' };
    } catch (err) {
      console.error('getQuoteByNumber error:', err.message || err);
      return {
        success: false,
        error: err.message || 'An unexpected error occurred while fetching the quote.',
      };
    }
  };

  // Get paginated, filterable list of quotes
// Get paginated, filterable list of quotes
const getQuotes = async (params = {}) => {
  try {
    const query = new URLSearchParams(params).toString();
    const res = await get(`/quotes?${query}`, token);
    
    // Check if results is an array and handle the new response structure
    return Array.isArray(res?.results)
      ? { 
          success: true, 
          data: res,
          quotes: res.results,        // The actual quotes array
          pagination: {               // New pagination metadata
            total: res.total,
            page: res.page,
            pages: res.pages,
            limit: parseInt(params.limit) || 10
          }
        }
      : { 
          success: false, 
          error: res?.error || 'Failed to fetch quotes list.' 
        };
  } catch (err) {
    console.error('getQuotes error:', err.message || err);
    return {
      success: false,
      error: err.message || 'An unexpected error occurred while retrieving quotes.',
    };
  }
};

const getGlobalOptions = async () => {
  try {
    // Assuming token is available in the same scope/context
    const res = await get('/quotes/options', token);
    console.log('[DEBUG] Raw response from GET /quotes/options:', res);

    const data = res?.data || res;

    return data?._id
      ? { success: true, data }
      : { success: false, error: data?.error || 'Options not found' };
  } catch (err) {
    console.error('getGlobalOptions error:', err.message || err);
    return { success: false, error: err.message || 'Network error while fetching global options' };
  }
};

  // Update Global Options -  update or change those default values (currency, tax, discount, terms, etc.) in the system.
  const updateGlobalOptions = async (optionId, data) => {
    try {
      const res = await put(`/quotes/options/${optionId}`, data, token);
      return res?.acknowledged
        ? { success: true, data: res }
        : { success: false, error: res?.error || 'Failed to update options' };
    } catch (err) {
      console.error('updateGlobalOptions error:', err.message || err);
      return { success: false, error: err.message || 'Network error while updating global options' };
    }
  };

  const generateQuotePdf = async (html, quote_number, overwrite = false) => {
  try {
    const res = await post('/quotes/pdf', { html, quote_number, overwrite }, token);
    
    // Handle 409 Conflict (PDF already exists)
    if (res.status === 409) {
      return { 
        success: false, 
        error: res.data?.error || 'PDF already exists for this quote number',
        errorType: 'exists',
        data: res.data?.url ? { url: res.data.url } : null
      };
    }

    // Handle other error responses
    if (res.error || res.status >= 400) {
      return {
        success: false,
        error: res.error || 'Failed to generate PDF',
        errorType: 'generation',
        data: res.url ? { url: res.url } : null
      };
    }

    // Success case
    return { 
      success: true, 
      data: {
        url: res.url,
        bucket: res.bucket,
        key: res.key,
        bytes: res.bytes
      }
    };
  } catch (err) {
    console.error('generateQuotePdf error:', err);
    return {
      success: false,
      error: err.message || 'Network error while generating PDF',
      errorType: 'network'
    };
  }
};
  // ------------ Reports ---------------
  // Get email send events for reports
  const getEmailEvents = async (period = 'month') => {
    try {
      const res = await get(`/reports/email-events?period=${period}`, token);
      // api.js returns response.data on success, error.response on failure, or undefined on network error
      if (!res) {
        // No response at all (endpoint doesn't exist yet or network error)
        return { success: true, data: [] };
      }
      // If res has a status property, it's an error.response object (not the unwrapped data)
      if (res.status && res.status >= 400) {
        return { success: true, data: [] };
      }
      // Normal success: res is already response.data
      if (res.success) {
        return { success: true, data: res.data || [] };
      }
      return { success: true, data: [] };
    } catch (err) {
      console.error('getEmailEvents error:', err);
      return { success: true, data: [] };
    }
  };

  // ------------ Email ---------------
  // Email Quote PDF - send a quote PDF via email
  const emailQuotePdf = async (quoteNumber, { to, subject, message, brochure }) => {
    try {
      const res = await post(
        `/quotes/${encodeURIComponent(quoteNumber)}/email`,
        { to, subject, message, brochure },
        token
      );

      const data = res?.data || res;

      if (data?.success) {
        return { success: true, data };
      } else {
        return { success: false, error: data?.error || 'Failed to send quote email' };
      }
    } catch (err) {
      console.error('emailQuotePdf error:', err.message || err);
      return { success: false, error: err.message || 'Network error while sending quote email' };
    }
  };

  // ------------ IMAGES --------------
  // Get Layout Image - fetch a presigned URL for an image from backend
  const getLayoutImage = async (imageName) => {
    try {
      // Normalize the extension to lowercase .jpg
      imageName = imageName.replace(/\.(jpg|jpeg|JPG|JPEG)$/i, '.jpg');

      console.log("🌐 Calling API for image:", imageName);

      const res = await get(
        `/quotes/get-layout-image?image-name=${encodeURIComponent(imageName)}`,
        token
      );

      console.log('📥 Backend response for', imageName, ':', res);

      if (res?.url) {
        console.log('✅ Successfully got URL for', imageName, ':', res.url);
        return { 
          success: true, 
          url: res.url, 
          module_description: res.module_description ?? null,
          part_number: res.part_number ?? null  // Added part_number to the response
        };
      } else {
        console.log('❌ No URL in response for', imageName, ':', res);
        return { 
          success: false, 
          error: res?.error || 'Failed to fetch layout image URL' 
        };
      }
    } catch (err) {
      console.error('❌ getLayoutImage error for', imageName, ':', err.message || err);
      return { 
        success: false, 
        error: err.message || 'Network error while fetching layout image' 
      };
    }
  };

  // For Multiple Images
  const getLayoutImages = async (imageNames) => {
    try {
      if (!Array.isArray(imageNames) || imageNames.length === 0) {
        return {
          success: false,
          error: 'Invalid input: imageNames must be a non-empty array'
        };
      }

      console.log("🌐 Calling API for images:", imageNames);

      const response = await post(
        '/quotes/get-layout-images',
        { imageNames },
        token
      );

      console.log('📥 Backend response for images:', response);

      if (Array.isArray(response)) {
        return {
          success: true,
          results: response.map(item => ({
            imageName: item.imageName,
            url: item.url,
            module_description: item.module_description,
            part_number: item.part_number,  // Added part_number
            success: item.success,
            error: item.error
          }))
        };
      } else {
        console.log('❌ Unexpected response format:', response);
        return {
          success: false,
          error: 'Unexpected response format from server'
        };
      }
    } catch (err) {
      console.error('❌ getLayoutImages error:', err.message || err);
      return {
        success: false,
        error: err.message || 'Network error while fetching layout images'
      };
    }
  };

  // SAW Image
const getSawImageData = async (imageName) => {
  try {
    console.log("🌐 Calling API for saw image:", imageName);

    // Make GET request with token in headers
    const res = await get(
      `/quotes/saw-image-data?image-name=${encodeURIComponent(imageName)}`,
      token
    );

    console.log('📥 Backend response for', imageName, ':', res);

    if (res?.presigned_image_url) {
      console.log('✅ Successfully got presigned URL for', imageName, ':', res.presigned_image_url);
      return { success: true, url: res.presigned_image_url };
      // Removed data: res since we're only returning the URL now
    } else {
      console.log('❌ No presigned URL in response for', imageName, ':', res);
      return { success: false, error: res?.error || 'Failed to fetch saw image URL' };
    }
  } catch (err) {
    console.error('❌ getSawImageData error for', imageName, ':', err.message || err);
    return { success: false, error: err.message || 'Network error while fetching saw image URL' };
  }
};
  // GET Image With Details
  const getImageWithDetails = async (imageName) => {
    try {
      // Return cached result if available
      if (imageDetailResultCache.current.has(imageName)) {
        return imageDetailResultCache.current.get(imageName);
      }

      // Coalesce concurrent requests for the same imageName
      if (inFlightImageDetailRequests.current.has(imageName)) {
        return await inFlightImageDetailRequests.current.get(imageName);
      }

      const requestPromise = (async () => {
      // const imageName= "test.jpg"
      console.log("@333333",imageName)
      console.log("🌐 Calling API for conveyor layout image:", imageName);
      // Make GET request with token in headers
      const res = await get(
        `/quotes/module-conveyors/layout-image?image-name=${encodeURIComponent(imageName)}`,
        token
      );

      console.log('📥 Backend response for', imageName, ':', res);

      if (res?.presigned_url) {
        console.log('✅ Successfully got presigned URL for', imageName, ':', res.presigned_url);
        const result = { 
          success: true, 
          url: res.presigned_url, 
          data: res,
          conveyorData: res // The entire conveyor object including presigned_url
        };
        imageDetailResultCache.current.set(imageName, result);
        return result;
      } else {
        console.log('❌ No presigned URL in response for', imageName, ':', res);
        const result = { 
          success: false, 
          error: res?.error || 'Failed to fetch conveyor layout data' 
        };
        imageDetailResultCache.current.set(imageName, result);
        return result;
      }
      })();

      inFlightImageDetailRequests.current.set(imageName, requestPromise);
      const out = await requestPromise;
      inFlightImageDetailRequests.current.delete(imageName);
      return out;
    } catch (err) {
      // console.error('❌ getImageWithDetails error for', imageName, ':', err.message || err);
      inFlightImageDetailRequests.current.delete(imageName);
      return { 
        success: false, 
        error: err.message || 'Network error while fetching conveyor layout data' 
      };
    }
  };
  // CATALOG — SKU master price list =========================
  // The clean ~101-item catalog behind the SKU pricing refactor. Editing a price here updates
  // it for EVERY image filename / orientation / variant that maps to that SKU, instantly.
  const getCatalogProducts = async () => {
    try {
      const res = await get('/catalog', token);
      if (Array.isArray(res)) return { success: true, products: res };
      return { success: false, error: (res && res.data && res.data.error) || 'Failed to fetch catalog' };
    } catch (error) {
      console.error('Error fetching catalog products:', error);
      return { success: false, error: 'Failed to fetch catalog' };
    }
  };

  const updateCatalogPrice = async (sku, data) => {
    try {
      const res = await patch(`/catalog/${encodeURIComponent(sku)}`, data, token);
      if (res && res.sku) return { success: true, product: res };
      return { success: false, error: (res && res.data && res.data.error) || 'Failed to update price' };
    } catch (error) {
      console.error('Error updating catalog price:', error);
      return { success: false, error: 'Failed to update price' };
    }
  };

  // MODULAR CONVEYORS ========================================
  // ================= GET all conveyors =================
const getModularConveyors = async () => {
  try {
    const res = await get('/modular-conveyors/getmodulars', token);
    
    if (Array.isArray(res)) {
      // Process the response to handle presigned URLs
      const conveyors = res.map(conveyor => ({
        ...conveyor,
        // The image URL is now available as presigned_url
        // You can add additional processing if needed
        imageUrl: conveyor.presigned_url || null
      }));
      
      return { success: true, conveyors };
    } else {
      return { success: false, error: 'Failed to fetch conveyors' };
    }
  } catch (error) {
    console.error('Error fetching modular conveyors:', error);
    return { success: false, error: 'Failed to fetch conveyors' };
  }
};

  // ================= CREATE a new conveyor =================
  const createModularConveyor = async (conveyorData) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();

      // Append all conveyor data fields to FormData
      Object.keys(conveyorData).forEach(key => {
        if (key === 'image' && conveyorData[key] instanceof File) {
          formData.append('image', conveyorData[key]); // append file
        } else if (conveyorData[key] !== null && conveyorData[key] !== undefined) {
          formData.append(key, conveyorData[key].toString()); // append other fields
        }
      });

      // Use your post function but don't set Content-Type header
      // The browser will automatically set it with the correct boundary for FormData
      const res = await post('/modular-conveyors/createModular', formData, token);
      
      if (res._id) {
        return { success: true, conveyor: res };
      } else {
        return { success: false, error: 'Failed to create conveyor' };
      }
    } catch (error) {
      console.error('Error creating modular conveyor:', error);
      return { success: false, error: 'Failed to create conveyor' };
    }
  };

  // ================= UPDATE a conveyor =================
  const updateModularConveyor = async (id, conveyorData) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();

      // Append all conveyor data fields to FormData
      Object.keys(conveyorData).forEach(key => {
        if (key === 'image' && conveyorData[key] instanceof File) {
          formData.append('image', conveyorData[key]);
        } else if (key === 'iso_image' && conveyorData[key] instanceof File) {
          formData.append('iso_image', conveyorData[key]);
        } else if (conveyorData[key] !== null && conveyorData[key] !== undefined) {
          formData.append(key, conveyorData[key].toString());
        }
      });

      const res = await patch(`/modular-conveyors/updateModular/${id}`, formData, token);
      
      if (res._id) {
        return { success: true, conveyor: res };
      } else {
        return { success: false, error: 'Failed to update conveyor' };
      }
    } catch (error) {
      console.error('Error updating modular conveyor:', error);
      return { success: false, error: 'Failed to update conveyor' };
    }
  };

  // ================= DELETE a conveyor =================
  const deleteModularConveyor = async (id) => {
    try {
      const res = await del(`/modular-conveyors/deleteModular/${id}`, token);
      
      if (res.message === 'Modular conveyor deleted successfully') {
        return { success: true };
      } else {
        return { success: false, error: 'Failed to delete conveyor' };
      }
    } catch (error) {
      console.error('Error deleting modular conveyor:', error);
      return { success: false, error: 'Failed to delete conveyor' };
    }
  };

  // ================== Brochures ====================
  const getBrochure = async (pdfName) => {
    try {
      console.log("🌐 Calling API for brochure:", pdfName);

      // Make API call with authentication
      const res = await get(
        `/quotes/brochures?pdfName=${encodeURIComponent(pdfName)}`,
        token
      );

      console.log('📥 Backend response for', pdfName, ':', res);

      if (res?.url) {
        console.log('✅ Successfully got URL for', pdfName, ':', res.url);
        return { 
          success: true, 
          url: res.url,
          pdfName: pdfName  // Include the PDF name in response
        };
      } else {
        console.log('❌ No URL in response for', pdfName, ':', res);
        return { 
          success: false, 
          error: res?.error || 'Failed to fetch brochure' 
        };
      }
    } catch (err) {
      console.error('❌ getBrochureUrl error for', pdfName, ':', err.message || err);
      return { 
        success: false, 
        error: err.message || 'Network error while fetching brochure URL' 
      };
    }
  };

  return (
    <DashboardContext.Provider value={{
      isLoading,
      error,
      CreateUser,
      getQuotePdf,
      createCustomer,
      getCatalogProducts,
      updateCatalogPrice,
      getModularConveyors,
      createModularConveyor,
      updateModularConveyor,
      deleteModularConveyor,
      getImageWithDetails,
      updateQuoteVersion,
      getSawImageData,
      emailQuotePdf,
      getEmailEvents,
      generateQuotePdf,
      getLayoutImage,
      createQuote,
      updateGlobalOptions,
      getGlobalOptions,
      getQuotes,
      getQuoteByNumber,
      getQuoteVersions,
      createQuoteRevision,
      allUsers,
      deleteUser,
      getAllCustomers,
      updateCustomer,
      updateCustomerNotes,
      updateCustomerFull,
      addCustomerLocation,
      deleteCustomer,
      getAllProducts,
      getSawProducts,
      getAllSaws,
      createSaw,
      updateSaw,
      deleteSaw,
      getCosenLibrary,
      getCosenFile,
      uploadQuoteBrochure,
      getBrochureFile,
      getAllQuotes,
      updateUserPassword,
      getMultipleData,
      getQuoteNumber,
      updateOrCreateQuote,
      getQuoteById,
      getLayoutImages,
      getBrochure,
      changeOwnPassword,
      deleteQuote
    }}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => useContext(DashboardContext);